/**
 * wishlist.js
 *
 * Client-side wishlist using localStorage. Supports:
 *   1. Toggle buttons — every [data-wishlist-toggle] persists its product
 *      handle in localStorage and syncs aria-pressed.
 *   2. Header count badge — [data-wishlist-count] auto-updates.
 *   3. Side drawer — [data-wishlist-drawer-open] opens the drawer; items
 *      are fetched lazily from /products/{handle}.js when the drawer opens.
 *
 * Storage key: `kissa_wishlist` — JSON array of product handles.
 *
 * Drawer DOM contract (rendered by snippets/wishlist-drawer.liquid):
 *   [data-wishlist-drawer]            outer container; toggled via [hidden]
 *   [data-wishlist-drawer-open]       opener button (in header)
 *   [data-wishlist-drawer-close]      any close trigger (X, overlay)
 *   [data-wishlist-items]             <ul> populated by JS
 *   [data-wishlist-empty]             empty-state container
 *   [data-wishlist-loading]           loading-state container
 *   [data-wishlist-item-template]     <template> with item markup
 *
 * If/when a backend wishlist exists, swap readState/writeState with API
 * calls; the rest of the contract stays.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'kissa_wishlist';
  var CART_ADD_URL = '/cart/add.js';

  /* ------------------------------------------------------------------
     STATE
     ------------------------------------------------------------------ */
  function readState() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeState(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* private-mode / quota — fail silently. */
    }
  }

  function broadcast(handles) {
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { handles: handles.slice() } })
    );
  }

  /* ------------------------------------------------------------------
     TOGGLE BUTTONS — every heart on cards + PDP
     ------------------------------------------------------------------ */
  function syncButton(btn, isActive) {
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    btn.classList.toggle('is-active', isActive);
    var ariaAdd = btn.dataset.ariaAdd || 'Add to wishlist';
    var ariaRemove = btn.dataset.ariaRemove || 'Remove from wishlist';
    btn.setAttribute('aria-label', isActive ? ariaRemove : ariaAdd);
  }

  function syncAllButtons(state) {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.dataset.productHandle;
      if (!handle) return;
      syncButton(btn, state.indexOf(handle) !== -1);
    });
  }

  function initToggle(btn) {
    var handle = btn.dataset.productHandle;
    if (!handle) return;

    syncButton(btn, readState().indexOf(handle) !== -1);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation(); /* product card hearts are nested inside an <a> wrapper — don't navigate. */
      var current = readState();
      var idx = current.indexOf(handle);
      if (idx === -1) current.push(handle);
      else current.splice(idx, 1);
      writeState(current);
      syncAllButtons(current); /* sync all hearts for this handle */
      updateCount(current.length);
      broadcast(current);
    });
  }

  /* ------------------------------------------------------------------
     COUNT BADGE
     ------------------------------------------------------------------ */
  function updateCount(n) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (badge) {
      badge.textContent = String(n);
      badge.classList.toggle('is-empty', n === 0);
    });
  }

  /* ------------------------------------------------------------------
     DRAWER
     ------------------------------------------------------------------ */
  var drawer = null;
  var lastFocused = null;

  function setDrawer(el) { drawer = el; }

  function openDrawer() {
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('wishlist-drawer-open');
    document.querySelectorAll('[data-wishlist-drawer-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'true');
    });
    /* Move focus to the close button for keyboard users */
    var closeBtn = drawer.querySelector('.wishlist-drawer__close');
    if (closeBtn) closeBtn.focus();

    renderItems();
  }

  function closeDrawer() {
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    document.body.classList.remove('wishlist-drawer-open');
    document.querySelectorAll('[data-wishlist-drawer-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function onKeydown(e) {
    if (!drawer || drawer.hidden) return;
    if (e.key === 'Escape') closeDrawer();
  }

  /* ------------------------------------------------------------------
     ITEM RENDERING
     Fetches each product via /products/HANDLE.js, fills the template.
     ------------------------------------------------------------------ */
  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('404');
        return r.json();
      })
      .catch(function () { return null; }); /* removed/unpublished — skip */
  }

  function formatMoney(cents) {
    /* Use Shopify.formatMoney if loaded; otherwise basic fallback */
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function renderItems() {
    if (!drawer) return;
    var listEl = drawer.querySelector('[data-wishlist-items]');
    var emptyEl = drawer.querySelector('[data-wishlist-empty]');
    var loadingEl = drawer.querySelector('[data-wishlist-loading]');
    /* Template lives as a sibling of the drawer, not a descendant. */
    var template = document.querySelector('[data-wishlist-item-template]');
    if (!listEl || !emptyEl || !template) return;

    var handles = readState();

    /* Reset — use replaceChildren() (safe, no innerHTML) */
    listEl.replaceChildren();
    emptyEl.hidden = true;

    if (handles.length === 0) {
      emptyEl.hidden = false;
      if (loadingEl) loadingEl.hidden = true;
      return;
    }

    if (loadingEl) loadingEl.hidden = false;

    Promise.all(handles.map(fetchProduct)).then(function (products) {
      if (loadingEl) loadingEl.hidden = true;

      var stillEmpty = true;
      products.forEach(function (product, i) {
        if (!product) return; /* skip null (deleted product) */
        stillEmpty = false;

        var node = template.content.firstElementChild.cloneNode(true);
        var img = node.querySelector('[data-wishlist-item-image]');
        var links = node.querySelectorAll('[data-wishlist-item-link]');
        var price = node.querySelector('[data-wishlist-item-price]');
        var addBtn = node.querySelector('[data-wishlist-item-add]');
        var removeBtn = node.querySelector('[data-wishlist-item-remove]');

        node.dataset.productHandle = product.handle;
        var firstVariant = (product.variants && product.variants[0]) || null;

        if (img && product.featured_image) {
          img.src = product.featured_image;
          img.alt = product.title;
        }

        links.forEach(function (a) {
          a.href = product.url || ('/products/' + product.handle);
          if (a.tagName === 'A' && !a.dataset.titleSet && a.querySelector('img') === null) {
            a.textContent = product.title;
            a.dataset.titleSet = '1';
          }
        });

        if (price) {
          price.textContent = formatMoney((firstVariant && firstVariant.price) || product.price || 0);
        }

        if (addBtn) {
          if (!firstVariant || firstVariant.available === false) {
            addBtn.disabled = true;
            addBtn.textContent = (window.kissaT && window.kissaT.soldOut) || 'Sold out';
          } else {
            addBtn.addEventListener('click', function () {
              addToCart(firstVariant.id, node, addBtn);
            });
          }
        }

        if (removeBtn) {
          removeBtn.addEventListener('click', function () {
            removeHandle(product.handle);
            node.remove();
            if (listEl.children.length === 0) {
              emptyEl.hidden = false;
            }
          });
        }

        listEl.appendChild(node);
      });

      if (stillEmpty) {
        /* All saved handles 404'd (products deleted) — show empty + cleanup */
        emptyEl.hidden = false;
        writeState([]);
        updateCount(0);
        broadcast([]);
        syncAllButtons([]);
      }
    });
  }

  function removeHandle(handle) {
    var current = readState();
    var idx = current.indexOf(handle);
    if (idx === -1) return;
    current.splice(idx, 1);
    writeState(current);
    updateCount(current.length);
    broadcast(current);
    syncAllButtons(current);
  }

  function addToCart(variantId, itemNode, btn) {
    var origText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.textContent = 'Added';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        /* Refresh header cart count if its listener exists */
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.querySelectorAll('[data-cart-count]').forEach(function (el) {
              el.textContent = cart.item_count;
              el.classList.toggle('is-empty', cart.item_count === 0);
            });
          });
      })
      .catch(function () { btn.textContent = 'Error'; })
      .finally(function () {
        setTimeout(function () { btn.disabled = false; btn.textContent = origText; }, 1500);
      });
  }

  /* ------------------------------------------------------------------
     INIT
     ------------------------------------------------------------------ */
  function init() {
    /* Initialize all toggles (cards + PDP) */
    document.querySelectorAll('[data-wishlist-toggle]').forEach(initToggle);

    /* Initial count badge */
    updateCount(readState().length);

    /* Drawer */
    var drawerEl = document.querySelector('[data-wishlist-drawer]');
    if (drawerEl) {
      setDrawer(drawerEl);
      document.querySelectorAll('[data-wishlist-drawer-open]').forEach(function (b) {
        b.addEventListener('click', function (e) {
          /* Footer/header may render this as an <a href="#..."> — don't jump-scroll. */
          e.preventDefault();
          openDrawer();
        });
      });
      drawerEl.querySelectorAll('[data-wishlist-drawer-close]').forEach(function (b) {
        b.addEventListener('click', closeDrawer);
      });
      document.addEventListener('keydown', onKeydown);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
